import { MapPin, Clock, Truck, Package } from 'lucide-react';

const GlobalShipping = () => {
  const shippingFeatures = [
    {
      icon: MapPin,
      title: 'Tacoma, WA Hub',
      description: 'Primary fulfillment center strategically located',
    },
    {
      icon: Clock,
      title: '2-4 Day Production',
      description: 'Fast turnaround on most orders',
    },
    {
      icon: Truck,
      title: 'Worldwide Delivery',
      description: 'Ship to 150+ countries globally',
    },
    {
      icon: Package,
      title: 'Order Tracking',
      description: 'Real-time tracking for every shipment',
    },
  ];

  return (
    <section
      data-testid="global-shipping-section"
      className="py-24 md:py-32 bg-coastal-navy text-white relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-coastal-orange rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern
              id="grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="white"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Content */}
          <div>
            <h2
              data-testid="global-shipping-heading"
              className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold mb-6"
            >
              Global shipping,{' '}
              <span className="text-coastal-orange">speedy delivery</span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-10">
              Enjoy fast fulfillment of our bestselling items from our Tacoma,
              Washington hub, or customize our entire collection with fulfillment
              from our international facilities.
            </p>

            {/* Features Grid */}
            <div
              data-testid="shipping-features"
              className="grid sm:grid-cols-2 gap-6"
            >
              {shippingFeatures.map((feature, index) => {
                const Icon = feature.icon;

                return (
                  <div
                    key={index}
                    data-testid={`shipping-feature-${index}`}
                    className="flex gap-4"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-coastal-orange" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-white mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-white/60 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right - Globe Visual */}
          <div
            data-testid="globe-visual"
            className="relative flex items-center justify-center"
          >
            {/* Globe */}
            <div className="relative w-80 h-80 md:w-96 md:h-96">
              {/* Outer Ring */}
              <div className="absolute inset-0 border-2 border-white/20 rounded-full animate-pulse" />
              <div className="absolute inset-4 border border-white/10 rounded-full" />
              <div className="absolute inset-8 border border-white/5 rounded-full" />

              {/* Center Globe */}
              <div className="absolute inset-12 bg-gradient-to-br from-coastal-orange/20 to-white/5 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/10">
                <svg
                  className="w-24 h-24 text-white/80"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              {/* Location Pins */}
              <div className="absolute top-8 left-1/2 -translate-x-1/2">
                <div className="w-3 h-3 bg-coastal-orange rounded-full animate-ping" />
                <div className="absolute top-0 w-3 h-3 bg-coastal-orange rounded-full" />
              </div>

              <div className="absolute bottom-16 left-8">
                <div className="w-2 h-2 bg-white/60 rounded-full" />
              </div>

              <div className="absolute top-1/3 right-4">
                <div className="w-2 h-2 bg-white/60 rounded-full" />
              </div>

              <div className="absolute bottom-1/4 right-12">
                <div className="w-2 h-2 bg-white/60 rounded-full" />
              </div>
            </div>

            {/* Tacoma Badge */}
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white text-coastal-navy rounded-xl px-6 py-3 shadow-xl">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-coastal-orange" />
                <span className="font-heading font-semibold">
                  Tacoma, Washington
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GlobalShipping;
